import { notifyError } from "@/lib/notify-error";
import { undoRedoErrorMessage } from "@/lib/undo-redo-error";

import { areGeometriesEqual, computeValue } from "../measurement-overlay-helpers";
import type {
  MeasurementGeometry,
  MeasurementOverlayRecord,
  PolygonGeometry,
} from "../measurement-overlay-types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";

type PreviewGeometryMap = Record<string, MeasurementGeometry | PolygonGeometry>;

interface CommitPreviewMeasurementUpdatesArgs {
  previewGeometries: PreviewGeometryMap;
  measurements: MeasurementOverlayRecord[];
  scale: MeasurementScale | null;
  valueIncrementFeet?: number;
  preserveMeasurementValues?: boolean;
  onUpdateMeasurement: (
    measurementId: MeasurementOverlayRecord["_id"],
    geometry: MeasurementGeometry | PolygonGeometry,
    height: number | undefined,
    value: number
  ) => Promise<void>;
}

export async function commitPreviewMeasurementUpdates({
  previewGeometries,
  measurements,
  scale,
  valueIncrementFeet,
  preserveMeasurementValues = false,
  onUpdateMeasurement,
}: CommitPreviewMeasurementUpdatesArgs): Promise<boolean> {
  const pendingUpdates = Object.entries(previewGeometries)
    .map(([measurementId, geometry]) => {
      const measurement = measurements.find((item) => String(item._id) === measurementId);
      if (!measurement || areGeometriesEqual(geometry, measurement.geometry)) {
        return null;
      }

      let nextValue = measurement.value;
      if (!preserveMeasurementValues && scale) {
        nextValue = computeValue(
          measurement.tool,
          measurement.measurementType,
          geometry,
          measurement.height,
          scale.pixelsPerFoot,
          scale.pageAspectRatio,
          valueIncrementFeet
        );
      }
      return { measurement, geometry, nextValue };
    })
    .filter((update): update is {
      measurement: MeasurementOverlayRecord;
      geometry: MeasurementGeometry | PolygonGeometry;
      nextValue: number;
    } => !!update);

  if (pendingUpdates.length === 0) {
    return true;
  }

  const results = await Promise.allSettled(
    pendingUpdates.map(async (update) =>
      onUpdateMeasurement(
        update.measurement._id,
        update.geometry,
        update.measurement.height,
        update.nextValue
      )
    )
  );
  const failure = results.find((result) => result.status === "rejected");
  if (failure) {
    console.error("Measurement update failed:", failure.reason);
    notifyError(failure.reason, undoRedoErrorMessage(failure.reason, "Failed to update measurement"));
    return false;
  }
  return true;
}
